import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DashboardLayout from './layout/dashboardLayout';
import LoadingOverlay from '../components/loading/loading';
import service from '../services/service';

// Modal Component
const CommissionModal = ({ isOpen, onClose, onSubmit, userId }) => {
    const [rate, setRate] = useState('');
    const [error, setError] = useState('');

    const modalStyles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: isOpen ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modal: {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        title: {
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#111827'
        },
        input: {
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`,
            marginBottom: '8px',
            fontSize: '14px'
        },
        error: {
            color: '#ef4444',
            fontSize: '12px',
            marginBottom: '16px'
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '16px'
        },
        button: {
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            border: '1px solid #e5e7eb',
        },
        cancelButton: {
            backgroundColor: '#ffffff',
            color: '#374151',
        },
        submitButton: {
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            border: 'none',
        }
    };

    const handleRateChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setRate(value);
            setError('');
        }
    };

    const handleSubmit = () => {
        if (!rate) {
            setError('Please enter a commission rate');
            return;
        }

        const numRate = parseFloat(rate);
        if (isNaN(numRate) || numRate < 0 || numRate > 100) {
            setError('Please enter a valid rate between 0 and 100');
            return;
        }

        onSubmit(userId, numRate);
        setRate('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={modalStyles.overlay} onClick={onClose}>
            <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
                <h3 style={modalStyles.title}>Set Commission Rate</h3>
                <div style={{ position: 'relative' }}>
                    <input
                        type="text"
                        value={rate}
                        onChange={handleRateChange}
                        placeholder="Enter commission rate"
                        style={modalStyles.input}
                    />
                    <span style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6b7280'
                    }}>%</span>
                </div>
                {error && <p style={modalStyles.error}>{error}</p>}
                <div style={modalStyles.buttonContainer}>
                    <button
                        onClick={onClose}
                        style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        style={{ ...modalStyles.button, ...modalStyles.submitButton }}
                    >
                        Set Rate
                    </button>
                </div>
            </div>
        </div>
    );
};

// BalanceModal Component
const BalanceModal = ({ isOpen, onClose, onSubmit, userId }) => {
    const [amount, setAmount] = useState('');
    const [error, setError] = useState('');

    const modalStyles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: isOpen ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modal: {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        title: {
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '16px',
            color: '#111827'
        },
        input: {
            width: '100%',
            padding: '8px 12px',
            borderRadius: '6px',
            border: `1px solid ${error ? '#ef4444' : '#e5e7eb'}`,
            marginBottom: '8px',
            fontSize: '14px'
        },
        error: {
            color: '#ef4444',
            fontSize: '12px',
            marginBottom: '16px'
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '16px'
        },
        button: {
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            border: '1px solid #e5e7eb',
        },
        cancelButton: {
            backgroundColor: '#ffffff',
            color: '#374151',
        },
        submitButton: {
            backgroundColor: '#07851c',
            color: '#ffffff',
            border: 'none',
        },
        submitButtonC: {
            backgroundColor: '#e91919',
            color: '#ffffff',
            border: 'none',
        }
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setAmount(value);
            setError('');
        }
    };

    const handleAction = (type) => {
        if (!amount) {
            setError('Please enter an amount');
            return;
        }

        const numAmount = parseFloat(amount);
        if (isNaN(numAmount) || numAmount <= 0) {
            setError('Please enter a valid positive amount');
            return;
        }

        onSubmit(type, userId, numAmount);
        setAmount('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={modalStyles.overlay} onClick={onClose}>
            <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={modalStyles.title}>Update Balance</h3>
                <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="Enter amount"
                    style={modalStyles.input}
                />
                {error && <p style={modalStyles.error}>{error}</p>}
                <div style={modalStyles.buttonContainer}>
                    <button
                        onClick={onClose}
                        style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => handleAction('debit')}
                        style={{ ...modalStyles.button, ...modalStyles.submitButtonC }}
                    >
                        Debit
                    </button>
                    <button
                        onClick={() => handleAction('credit')}
                        style={{ ...modalStyles.button, ...modalStyles.submitButton }}
                    >
                        Credit
                    </button>

                </div>
            </div>
        </div>
    );
};


const UsersListPage = () => {
    const push = useNavigate()
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    // Add new state for modal
    const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
    const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const [bankDetails, setBankDetails] = useState({
        userId: "",
        accountHolderName: "",
        mobileNo: "",
        bankAccountNumber: "",
        bankName: "",
        ifscCode: "",
        upiId: "",
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    // Function to open the modal and fetch details
    const openModal = (id) => {
        setIsModalOpen(true);
        getBankDetailsByUserID(id); // Fetch details when opening
    };

    // Function to close the modal
    const closeModal = () => {
        setIsModalOpen(false);
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await service.view('admin/users');
            if (!response.success) {
                toast.error(response.message + ": " + response.error);
                return;
            }
            setData(response.data.users);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const getBankDetailsByUserID = useCallback(async (id) => {
        setLoading(true);
        try {
            const isLoggedIn = sessionStorage.getItem("isLoggedIn") === "true";
            if (isLoggedIn) {
                const response = await service.view(`bankDetails/user/${id}`);
                if (response.success && response.data) {
                    setBankDetails({
                        userId: response.data.userId,
                        accountHolderName: response.data.accountHolderName,
                        mobileNo: response.data.mobileNo,
                        bankAccountNumber: response.data.bankAccountNumber,
                        bankName: response.data.bankName,
                        ifscCode: response.data.ifscCode,
                        upiId: response.data.upiId,
                    });
                } else {
                    // Reset bank details if no data is returned
                    setBankDetails({
                        userId: "",
                        accountHolderName: "",
                        mobileNo: "",
                        bankAccountNumber: "",
                        bankName: "",
                        ifscCode: "",
                        upiId: "",
                    });
                }
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleActionButton = (type, id) => {
        try {
            if (type === 'Edit') {
                push('/admin/user/update/' + id);
            } else if (type === 'block' || type === 'unblock') {
                const isConfirmed = window.confirm("Are you sure you want to " + type + " this User?");
                if (isConfirmed) blockUser(type, id);
            } else if (type === 'commission') {
                setSelectedUserId(id);
                setIsCommissionModalOpen(true);
            } else if (type === 'wallet') {
                createWallet(id);
            } else if (type === 'balance') {
                setSelectedUserId(id);
                setIsBalanceModalOpen(true);
            }
        } catch (error) {
            toast.error(error.message);
        }
    }

    const createWallet = async (userId) => {
        try {
            setLoading(true);
            const response = await service.save({ userId }, `wallet/`, "POST");
            if (response && !response.success) {
                toast.error(`${response.message}`);
                return;
            }
            toast.success(`${response.message}`);
            fetchUsers();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    // Add new function to handle commission rate submission
    const handleCommissionSubmit = async (userId, rate) => {
        try {
            setLoading(true);
            // Replace this with your actual API endpoint and service call
            const response = await service.save({ userId, rate }, `admin/commission-rate`);
            if (!response.success) {
                toast.error(`${response.message} ${response.error}`);
                return;
            }
            toast.success(`Commission rate updated successfully`);
            fetchUsers(); // Refresh the users list
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const blockUser = async (type, id) => {
        try {
            setLoading(true);
            const response = await service.view(`admin/users/${id}/${type}`, "GET");
            if (!response.success) {
                toast.error(`${response.message} ${response.error}`);
                return;
            }
            toast.success(`${response.message}`);
            fetchUsers();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBalanceUpdate = async (type, id, amount) => {
        try {
            setLoading(true);
            const response = await service.save({ userId: id, type, amount }, `admin/update-balance`);
            if (!response.success) {
                toast.error(`${response.message}`);
                return;
            }
            toast.success(`${response.message}`);
            fetchUsers();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }

    const styles = {
        container: {
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0',
        },
        searchContainer: {
            position: 'relative',
            width: '320px',
        },
        searchInput: {
            width: '100%',
            padding: '10px 16px 10px 40px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '14px',
            outline: 'none',
            transition: 'all 0.2s',
        },
        tableContainer: {
            overflowX: 'auto',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            textAlign: 'left',
        },
        th: {
            padding: '14px 20px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
            fontWeight: '600',
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '14px 20px',
            borderBottom: '1px solid #e5e7eb',
            color: '#374151',
        },
        badge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
        },
        actionButton: {
            padding: '6px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
            color: '#6b7280',
            transition: 'all 0.2s',
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '20px',
            padding: '10px 0',
        },
        pageButton: {
            padding: '8px 16px',
            margin: '0 4px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151',
            transition: 'all 0.2s',
        },
        icon: {
            fontSize: '16px',
            display: 'inline-block',
            cursor: 'pointer',
            userSelect: 'none',
        },
        searchIcon: {
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#6b7280',
            fontSize: '14px',
        },
        actionIcon: {
            padding: '6px 8px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
            color: '#6b7280',
            transition: 'all 0.2s',
            fontSize: '16px',
            lineHeight: 1,
        },
        serialNo: {
            width: '70px',
            textAlign: 'center',
            color: '#6b7280',
            fontWeight: '500',
        }
    };

    // Custom Inline CSS
    const stylesM = {
        container: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
        },
        openButton: {
            backgroundColor: "#007bff",
            color: "white",
            fontSize: "16px",
            fontWeight: "bold",
            padding: "10px 20px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "0.3s",
        },
        overlay: {
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
        },
        modal: {
            backgroundColor: "white",
            width: "400px",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
            textAlign: "center",
            animation: "fadeIn 0.3s ease-in-out",
        },
        modalHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #ddd",
            paddingBottom: "10px",
        },
        modalTitle: {
            fontSize: "20px",
            fontWeight: "bold",
            color: "#333",
        },
        closeButton: {
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            color: "#888",
            transition: "0.3s",
        },
        modalBody: {
            padding: "20px",
            textAlign: "left",
        },
        loadingText: {
            textAlign: "center",
            fontSize: "16px",
            color: "#555",
        },
        details: {
            fontSize: "16px",
            color: "#333",
            lineHeight: "1.5",
        },
        modalFooter: {
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #ddd",
            paddingTop: "10px",
        },
        closeModalButton: {
            backgroundColor: "#dc3545",
            color: "white",
            fontSize: "14px",
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            cursor: "pointer",
            transition: "0.3s",
        },
    };

    // Define visible columns and their labels
    const columns = [
        { key: 'firstName', label: 'Name ', sortable: true },
        { key: 'blocked', label: 'Blocked ', sortable: true },
        { key: 'contact_number', label: 'Contact', sortable: false },
        { key: 'commissionRate', label: 'Commission Rate ', sortable: true },
        { key: 'datetime', label: 'Created At ', sortable: true },
    ];

    // Icons using Unicode symbols
    const icons = {
        search: '🔍',
        edit: '✏️',
        delete: '🗑️',
        block: '🚫',
        unblock: '🔓',
        more: '⋮',
        commission: '%',
        check: '✓',
        cross: '✕',
        sortUp: '▲',
        sortDown: '▼',
        sortBoth: '⇵',
        createWallet: '💳',
        bank: '🏦'
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatName = (firstName, lastName) => {
        if (!firstName && !lastName) return '-';
        return `${firstName} ${lastName}`;
    }

    const getBooleanBadgeStyle = (value) => ({
        ...styles.badge,
        backgroundColor: value ? '#def7ec' : '#fee2e2',
        color: value ? '#03543f' : '#991b1b',
    });

    const sortedAndFilteredData = useMemo(() => {
        let filteredData = [...data];

        if (searchTerm) {
            filteredData = filteredData.filter(item => {
                const searchFields = [
                    item.firstName,
                    item.lastName,
                    item.contact_number
                ];
                return searchFields.some(field =>
                    field?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                );
            });
        }

        if (sortConfig.key && sortConfig.key !== 'serialNo') {
            filteredData.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    }, [data, searchTerm, sortConfig]);

    const paginatedData = sortedAndFilteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const pageCount = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const renderCellContent = (column, item, index) => {
        switch (column.key) {
            case 'serialNo':
                return ((currentPage - 1) * itemsPerPage) + index + 1;
            case 'admin':
            case 'blocked':
                return (
                    <span style={getBooleanBadgeStyle(item[column.key])}>
                        {item[column.key] ? icons.check : icons.cross}
                        {' '}{item[column.key] ? 'Yes' : 'No'}
                    </span>
                );
            case 'datetime':
                return formatDate(item.createdAt);
            case 'firstName':
                return formatName(item.firstName, item.lastName);
            default:
                return item[column.key] || '-';
        }
    };

    return (
        <DashboardLayout>
            {loading && <LoadingOverlay />}
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.title}>User Management</h2>
                    <div style={styles.searchContainer}>
                        <span style={styles.searchIcon}>{icons.search}</span>
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        onClick={() => requestSort(column.key, column.sortable)}
                                        style={{
                                            ...styles.th,
                                            ...(column.key === 'serialNo' && styles.serialNo),
                                            cursor: column.sortable ? 'pointer' : 'default'
                                        }}
                                    >
                                        {column.label}
                                        {column.sortable && (
                                            <span style={styles.icon}>{sortConfig.key === column.key ?
                                                (sortConfig.direction === 'ascending' ? icons.sortUp : icons.sortDown)
                                                : icons.sortBoth}</span>
                                        )}
                                    </th>
                                ))}
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((item) => (
                                <tr
                                    key={item._id}
                                    style={{
                                        backgroundColor: 'white',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    {columns.map((column) => (
                                        <td key={column.key} style={styles.td}>
                                            {renderCellContent(column, item)}
                                        </td>
                                    ))}
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                style={styles.actionIcon}
                                                title="Edit User"
                                                onClick={() => handleActionButton('Edit', item._id)}
                                            >
                                                {icons.edit}
                                            </button>
                                            <button
                                                style={styles.actionIcon}
                                                title="Set Commission"
                                                onClick={() => handleActionButton('commission', item._id)}
                                            >
                                                {icons.commission}
                                            </button>
                                            <button
                                                style={styles.actionIcon}
                                                title="Update Balance"
                                                onClick={() => handleActionButton('balance', item._id)}>
                                                ₹
                                            </button>
                                            <button
                                                style={styles.actionIcon}
                                                title="Create Wallet"
                                                onClick={() => handleActionButton('wallet', item._id)}
                                            >
                                                {icons.createWallet}
                                            </button>
                                            <button
                                                style={styles.actionIcon}
                                                title="View Bank Details"
                                                onClick={() => openModal(item._id)}
                                            >
                                                {icons.bank}
                                            </button>
                                            {item.blocked ? (<button
                                                style={styles.actionIcon}
                                                title="Unblock User"
                                                onClick={() => handleActionButton('unblock', item._id)}
                                            >
                                                {icons.unblock}
                                            </button>) : (<button
                                                style={styles.actionIcon}
                                                title="Block User"
                                                onClick={() => handleActionButton('block', item._id)}
                                            >
                                                {icons.block}
                                            </button>)}

                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={styles.pagination}>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, sortedAndFilteredData.length)} of{' '}
                        {sortedAndFilteredData.length} entries
                    </span>
                    <div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            style={{
                                ...styles.pageButton,
                                opacity: currentPage === 1 ? 0.5 : 1,
                            }}
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount))}
                            disabled={currentPage === pageCount}
                            style={{
                                ...styles.pageButton,
                                opacity: currentPage === pageCount ? 0.5 : 1,
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            <CommissionModal
                isOpen={isCommissionModalOpen}
                onClose={() => {
                    setIsCommissionModalOpen(false);
                    setSelectedUserId(null);
                }}
                onSubmit={handleCommissionSubmit}
                userId={selectedUserId}
            />

            <BalanceModal
                isOpen={isBalanceModalOpen}
                onClose={() => {
                    setIsBalanceModalOpen(false);
                    setSelectedUserId(null);
                }}
                onSubmit={handleBalanceUpdate}
                userId={selectedUserId}
            />

            {/* Modal Popup */}
            {isModalOpen && (
                <div style={stylesM.overlay}>
                    <div style={stylesM.modal}>
                        {/* Modal Header */}
                        <div style={stylesM.modalHeader}>
                            <h3 style={stylesM.modalTitle}>Bank Details</h3>
                            <button style={stylesM.closeButton} onClick={closeModal}>✖</button>
                        </div>

                        {/* Modal Content */}
                        <div style={stylesM.modalBody}>
                            {loading ? (
                                <p style={stylesM.loadingText}>Loading...</p>
                            ) : bankDetails.accountHolderName || bankDetails.bankAccountNumber ? (
                                <div style={stylesM.details}>
                                    <p style={{ color: "black" }}><strong>Account Holder:</strong> {bankDetails.accountHolderName}</p>
                                    <p style={{ color: "black" }}><strong>Mobile:</strong> {bankDetails.mobileNo}</p>
                                    <p style={{ color: "black" }}><strong>Bank Account:</strong> {bankDetails.bankAccountNumber}</p>
                                    <p style={{ color: "black" }}><strong>Bank Name:</strong> {bankDetails.bankName}</p>
                                    <p style={{ color: "black" }}><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
                                    <p style={{ color: "black" }}><strong>UPI ID:</strong> {bankDetails.upiId}</p>
                                </div>
                            ) : (
                                <p style={{
                                    color: 'red',  // Customize the color
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    marginTop: '20px',
                                }}>No bank details available for this user.</p>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div style={stylesM.modalFooter}>
                            <button style={stylesM.closeModalButton} onClick={closeModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </DashboardLayout>
    );
};
export default UsersListPage;